package fyi.moko.hotpl.data.network

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import fyi.moko.hotpl.domain.model.normalizeForComparison
import fyi.moko.hotpl.domain.service.DuplicateKey
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

class FirebaseNetworkPlaceDataSource(
    emulatorHost: String? = null
) : NetworkPlaceDataSource {
    private val firestore = dev.gitlive.firebase.Firebase.firestore

    init {
        if (emulatorHost != null) {
            try {
                firestore.useEmulator(emulatorHost, 8080)
            } catch (e: Exception) {
                // Ignore re-configuration errors
            }
        }
    }

    override suspend fun savePlace(uid: String, place: RemotePlaceDto) {
        firestore.document("users/$uid/places/${place.id}").set(place)
    }

    override suspend fun deletePlace(uid: String, placeId: String, place: RemotePlaceDto?) {
        val docRef = firestore.document("users/$uid/places/$placeId")
        if (place != null) {
            docRef.set(place)
        } else {
            docRef.delete()
        }
    }

    override suspend fun placeExists(uid: String, placeId: String): Boolean =
        firestore.document("users/$uid/places/$placeId").get().exists

    override suspend fun savePublicProjection(uid: String, place: PublicPlaceDto) {
        firestore.document("public_places/${uid}_${place.placeId}").set(place)
    }

    override suspend fun deletePublicProjection(uid: String, placeId: String) {
        firestore.document("public_places/${uid}_$placeId").delete()
    }

    override suspend fun getPublicProjection(uid: String, placeId: String): PublicPlaceDto? {
        val snapshot = firestore.document("public_places/${uid}_$placeId").get()
        if (!snapshot.exists) return null
        return try {
            snapshot.data<PublicPlaceDto>()
        } catch (e: Exception) {
            // A projection this client can't decode still has to be replaced, so treat it as
            // absent; any shared copies it listed are swept by the next cover change.
            null
        }
    }

    override fun observePlaces(uid: String): Flow<List<RemotePlaceDto>> {
        return firestore.collection("users/$uid/places").snapshots.map { snapshot ->
            snapshot.documents.mapNotNull { doc ->
                try {
                    doc.data<RemotePlaceDto>()
                } catch (e: Exception) {
                    // Tolerated (one bad doc must not kill the stream), but never silently: if
                    // every doc fails to decode the symptom is an empty home screen with no
                    // error anywhere, which is indistinguishable from "no data" (web, 2026-07-13).
                    println("FirebaseNetworkPlaceDataSource: place doc ${doc.id} failed to decode: ${e.message ?: e::class.simpleName}")
                    null
                }
            }
        }
    }

    /**
     * One-shot read of the document ids under `users/{uid}/places`. Read from the document id
     * rather than the payload so a place whose body fails to deserialize still reserves its id.
     */
    override suspend fun placeIdsFor(uid: String): Set<String> =
        firestore.collection("users/$uid/places").get().documents
            .map { it.id }
            .toSet()

    /**
     * A second full-collection read (on top of [placeIdsFor]'s) is intentional here: it decodes
     * the full [RemotePlaceDto] rather than just the doc id, and only [LinkAccountUseCase] calls
     * it, on the same account-switch path that already reads the whole collection once.
     */
    override suspend fun duplicateKeysFor(uid: String): Map<DuplicateKey, String> =
        firestore.collection("users/$uid/places").get().documents.mapNotNull { doc ->
            try {
                val place = doc.data<RemotePlaceDto>()
                val lat = place.latitude
                val lng = place.longitude
                if (lat != null && lng != null) {
                    DuplicateKey(normalizeForComparison(place.name), lat, lng) to doc.id
                } else {
                    null
                }
            } catch (e: Exception) {
                null
            }
        }.toMap()

    override suspend fun saveVisit(uid: String, placeId: String, visit: RemoteVisitDto) {
        firestore.document("users/$uid/places/$placeId/visits/${visit.id}").set(visit)
    }

    override suspend fun deleteVisit(uid: String, placeId: String, visitId: String) {
        firestore.document("users/$uid/places/$placeId/visits/$visitId").delete()
    }

    override fun observeVisits(uid: String, placeId: String): Flow<List<RemoteVisitDto>> =
        firestore.collection("users/$uid/places/$placeId/visits").snapshots.map { snapshot ->
            snapshot.documents.mapNotNull { doc ->
                try {
                    doc.data<RemoteVisitDto>()
                } catch (e: Exception) {
                    // Same reasoning as the place decode catch above.
                    println("FirebaseNetworkPlaceDataSource: visit doc ${doc.id} failed to decode: ${e.message ?: e::class.simpleName}")
                    null
                }
            }
        }
}
